import { listProducts } from "@lib/data/products"
import { getRegion } from "@lib/data/regions"
import { HttpTypes } from "@medusajs/types"
import Product from "../product-preview"

type RelatedProductsProps = {
  product: HttpTypes.StoreProduct
  countryCode: string
}

async function fetchRecommendations(productId: string, regionId?: string): Promise<HttpTypes.StoreProduct[]> {
  try {
    const medusaUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || ''
    const params = new URLSearchParams({ type: 'crosssell', limit: '4' })
    if (regionId) params.set('region_id', regionId)
    const res = await fetch(`${medusaUrl}/store/products/${productId}/recommendations?${params}`, {
      next: { revalidate: 300 },
    })
    if (!res.ok) return []
    const data = await res.json()
    return data.recommendations ?? []
  } catch {
    return []
  }
}

export default async function RelatedProducts({
  product,
  countryCode,
}: RelatedProductsProps) {
  const region = await getRegion(countryCode)

  if (!region) {
    return null
  }

  // Try recommendation API first (crosssell)
  let products = await fetchRecommendations(product.id, region.id)

  // Fall back to collection/tag-based if insufficient results
  if (products.length < 4) {
    const queryParams: HttpTypes.StoreProductListParams = {}
    if (region?.id) {
      queryParams.region_id = region.id
    }
    if (product.collection_id) {
      queryParams.collection_id = [product.collection_id]
    }
    if (product.tags) {
      queryParams.tag_id = product.tags
        .map((t) => t.id)
        .filter(Boolean) as string[]
    }
    queryParams.is_giftcard = false

    const fallback = await listProducts({
      queryParams,
      countryCode,
    }).then(({ response }) => {
      const recIds = new Set(products.map((p) => p.id))
      return response.products.filter(
        (p) => p.id !== product.id && !recIds.has(p.id)
      )
    })

    products = [...products, ...fallback].slice(0, 8)
  }

  if (!products.length) {
    return null
  }

  return (
    <div className="product-page-constraint">
      <div className="flex flex-col items-center text-center mb-16">
        <span className="text-base-regular text-gray-600 mb-6">
          Related products
        </span>
        <p className="text-2xl-regular text-ui-fg-base max-w-lg">
          You might also want to check out these products.
        </p>
      </div>

      <ul className="grid grid-cols-2 small:grid-cols-3 medium:grid-cols-4 gap-x-6 gap-y-8">
        {products.map((product) => (
          <li key={product.id}>
            <Product region={region} product={product} />
          </li>
        ))}
      </ul>
    </div>
  )
}
